// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xProductDetailsPartNumberStyles } from "@adl/App/SharedStyle/ProductDetailsPartNumberStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = {
    product: ProductModel;
};

export interface ProductDetailsHeaderContentStyles {
    container?: InjectableCss;
    imgSection?: InjectableCss;
    detailsSection?: InjectableCss;
    productTitle?: TypographyPresentationProps;
    productParts?: InjectableCss;
    partContainer?: InjectableCss;
    partName?: TypographyPresentationProps;
    partValue?: TypographyPresentationProps;
}

export const productDetailsHeaderContentStyles: ProductDetailsHeaderContentStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },

    imgSection: {
        css: css`
            flex: 0 0 60px;
            margin-right: 28px;
            > div {
                min-height: auto !important; // important in case of sriv
                height: auto;
                & > div {
                    min-height: auto;
                }
            }
            &&&& video,
            &.product-header-summary iframe { // add class name to increase the specifity 
                max-height: 60px;
                min-height: 60px;
            }
        `,
    },
    detailsSection: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    productTitle: {
        variant: "h6",
        css: css`
            margin-bottom: 8px;
        `,
    },
    productParts: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    partContainer: {
        css: css`
            margin-right: 34px;
            &:last-child {
                margin: 0;
            }
        `,
    },
    partName: { ...xProductDetailsPartNumberStyles.labelText },
    partValue: { ...xProductDetailsPartNumberStyles.valueText },
};

const styles = productDetailsHeaderContentStyles;

const renderPart = (label: string, value?: string) => {
    return (
        value && (
            <StyledWrapper {...styles.partContainer}>
                <Typography {...styles.partName}>{label}</Typography>
                <Typography {...styles.partValue}>{value || "#"}</Typography>
            </StyledWrapper>
        )
    );
};

const ProductDetailsSummary: FC<Props> = ({ product }) => {
    if (!product) {
        return null;
    }
    const { brand, productTitle, manufacturerItem, productNumber, properties } = product;
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.imgSection} className="product-header-summary">
                {createWidgetElement("ProductDetails/ProductDetailsPrimaryImage", {
                    fields: {},
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.detailsSection}>
                {productTitle && <Typography {...styles.productTitle}>{productTitle}</Typography>}
                <StyledWrapper {...styles.productParts}>
                    {renderPart(translate("Brand"), brand?.name)}
                    {renderPart(translate("MFG #"), manufacturerItem)}
                    {renderPart(translate("Part #"), productNumber)}
                    {/* TODO: make sure how this value will be returned */}
                    {renderPart(translate("Last Ordered"), properties.lastOrderDate)}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ProductDetailsSummary;
